import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** * GETTER : Extrait uniquement le nombre du texte (ex: "( 5 )" -> 5)
   */
  get currentCartCount() {
    const text = this.refs.cartBubbleCount.textContent ?? '0';
    // On supprime tout ce qui n'est pas un chiffre
    const cleanNumber = text.replace(/\D/g, ''); 
    return parseInt(cleanNumber || '0', 10);
  }

  /** * SETTER : Formate le nombre avec les parenthèses (ex: 5 -> "( 5 )")
   */
  set currentCartCount(value) {
    const numericValue = parseInt(value, 10);
    const displayValue = numericValue < 100 ? String(numericValue) : '99+';
    this.refs.cartBubbleCount.textContent = `( ${displayValue} )`;
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    
    // On synchronise dès le départ pour éviter les sauts visuels
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    // Mise à jour directe
    this.renderCartBubble(itemCount, false);
  };

  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // Calcul du nouveau total
    const newCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;
    
    // L'assignation utilise maintenant le SETTER (avec parenthèses)
    this.currentCartCount = newCount;

    // Sécurité visuelle
    this.refs.cartBubble.classList.remove('visually-hidden');
    this.refs.cartBubbleCount.classList.remove('hidden');
    this.classList.toggle('header-actions__cart-icon--has-cart', newCount > 0);

    // On stocke la valeur brute (chiffre seul) dans la session
    sessionStorage.setItem('cart-count', JSON.stringify({
      value: String(newCount),
      timestamp: Date.now(),
    }));

    if (animate && newCount > 0) {
      this.refs.cartBubble.classList.add('cart-bubble--animating');
      await onAnimationEnd(this.refs.cartBubbleText);
      this.refs.cartBubble.classList.remove('cart-bubble--animating');
    }
  };

  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  ensureCartBubbleIsCorrect = () => {
    if (!this.refs.cartBubbleCount) return;
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    if (sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);
      // On ne met à jour que si les données sont fraîches (10s)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);
        if (!isNaN(count)) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (e) {
      console.error("Erreur de synchro panier:", e);
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}